import { inject, injectable } from 'tsyringe';
import type { TransportResponse } from 'atemoya-contracts';
import { ITransportService, TransportRequestTypes } from './ITransportService';

type PendingHandler = {
    resolve: (data: TransportResponse) => void;
    reject: (error: Error) => void;
};

@injectable()
export class WebSocketService implements ITransportService {
    private socket!: WebSocket;
    private pending: Map<string, PendingHandler> = new Map();
    private socketReady: Promise<void> | null = null;
    private manualClose = false;
    private reconnectAttempts = 0;
    private url: string;

    constructor(@inject('WS_URL') url: string) {
        this.url = url;
    }

    public async connect(): Promise<void> {
        this.manualClose = false;
        this.socketReady = this._openSocket();
        return this.socketReady;
    }

    private _openSocket(): Promise<void> {
        this.socket = new WebSocket(this.url);

        return new Promise((resolve, reject) => {
            this.socket.onopen = () => {
                console.log('[WebSocket] Connected');
                this.reconnectAttempts = 0;
                resolve();
            };

            this.socket.onerror = (err) => reject(err);

            this.socket.onmessage = (event) => {
                const msg: TransportResponse = JSON.parse(event.data);
                const requestId = msg?.identity?.clientRequestId as string | undefined;

                if (requestId) {
                    const handler = this.pending.get(requestId);
                    if (handler) {
                        handler.resolve(msg);
                        this.pending.delete(requestId);
                    } else {
                        console.warn(`[WebSocket] No handler for requestId: ${requestId}`);
                    }
                }
            };

            this.socket.onclose = () => {
                console.log('[WebSocket] Disconnected');

                this.pending.forEach((handler) => {
                    handler.reject(new Error('[WebSocket] Connection closed'));
                });
                this.pending.clear();

                if (!this.manualClose) {
                    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
                    this.reconnectAttempts++;
                    console.log(`[WebSocket] Reconnecting in ${delay}ms`);
                    this.socketReady = new Promise<void>((reconnectResolve, reconnectReject) => {
                        setTimeout(() => {
                            this._openSocket().then(reconnectResolve).catch(reconnectReject);
                        }, delay);
                    });
                }
            };
        });
    }

    public disconnect(): void {
        this.manualClose = true;
        this.socket?.close();
    }

    public async send(
        type: TransportRequestTypes,
        payload: Record<string, any>,
        clientRequestId: string
    ): Promise<TransportResponse> {
        if (!this.socketReady) {
            throw new Error('WebSocket is not connected. Call connect() first.');
        }
        await this.socketReady;

        const data = { type, payload, clientRequestId };
        return new Promise<TransportResponse>((resolve, reject) => {
            this.pending.set(clientRequestId, { resolve, reject });
            this.socket.send(JSON.stringify(data));
        });
    }
}
